/**
 * Phase 7 chunk 1B — pure math + locked constants for the sky-view
 * camera. Kept Cesium-free so Vitest can unit-test the angle math
 * without spinning up a viewer (matches the project's "Cesium-touching
 * code is verified via Playwright e2e" testing pattern; the Cesium
 * glue lives in `SkyCamera.ts` next door).
 *
 * Spec-locked values from docs/phase7.md § II decisions 5 + 9:
 *  - Pitch +75°  (stargazer tilt, 15° below true zenith — chosen over
 *    pure +90° "lying on your back" framing)
 *  - FOV  75°    (wider than Cesium's default 60° so a generous slice
 *    of sky is visible)
 *  - Heading 0°  (north up)
 *  - Roll    0°
 *  - Fly duration 1.5 s (instant cut under prefers-reduced-motion —
 *    Phase 8 chunk 1 wires that fallback)
 */

/** Locked sky-view camera pose, in degrees for human-readable defaults. */
export const SKY_VIEW_PITCH_DEG = 75;
export const SKY_VIEW_HEADING_DEG = 0;
export const SKY_VIEW_ROLL_DEG = 0;

/** Locked sky-view FOV in degrees. The camera frustum is widened from
 *  Cesium's default (~60°) to give a more open sky-feeling field. */
export const SKY_VIEW_FOV_DEG = 75;

/** Locked free-look FOV bounds in degrees. Scroll-wheel zoom (chunk 2)
 *  will interpolate within this range. */
export const SKY_VIEW_FOV_MIN_DEG = 30;
export const SKY_VIEW_FOV_MAX_DEG = 110;

/** Locked fly-to duration in seconds. Replaced with 0 (instant cut)
 *  when the user has prefers-reduced-motion set (Phase 8 chunk 1). */
export const SKY_VIEW_FLY_DURATION_S = 1.5;

/** Default observer altitude in meters when the observer record does
 *  not specify one. Sea level — the camera will sit a touch *above*
 *  the ground (matching the "stargazer standing in a field" mental
 *  model rather than "lying face-up in the ocean"). */
export const SKY_VIEW_DEFAULT_ALTITUDE_M = 0;

/** Eye-height bump applied above the observer's altitude when placing
 *  the camera in sky view. Without this lift the camera sits *on* the
 *  WGS84 ellipsoid (its near-clip plane is inside the globe geometry),
 *  so dragging the view down looks "through" the planet. 1.7 m
 *  matches a typical adult stargazer's eye height and is enough to
 *  ensure the globe ellipsoid properly occludes below-horizon views. */
export const SKY_VIEW_EYE_HEIGHT_M = 1.7;

/** Chunk 3E — duration of the orientation-only flyTo that aims the
 *  sky-view camera at a newly-selected satellite. Shorter than the
 *  1.5 s sky-view entry flight because we're only rotating, not
 *  repositioning. */
export const SKY_VIEW_AIM_DURATION_S = 0.8;

/**
 * Chunk 3E — compute camera heading (radians clockwise from north,
 * in [0, 2π)) from a local-ENU offset vector. atan2(east, north)
 * gives signed heading in (-π, π]; we normalize to [0, 2π) so the
 * value matches Cesium's camera.heading convention.
 *
 * Returns 0 (north) when both east and north are 0 (looking straight
 * up — heading is undefined; conventional default is N).
 */
export function headingFromEnu(east: number, north: number): number {
  if (!Number.isFinite(east) || !Number.isFinite(north)) return 0;
  if (east === 0 && north === 0) return 0;
  let h = Math.atan2(east, north);
  if (h < 0) h += 2 * Math.PI;
  return h;
}

/**
 * Chunk 3E — compute elevation angle (radians, [-π/2, π/2]) from a
 * local-ENU offset. asin(up / range). Returns 0 on zero-magnitude
 * input. Cesium's camera.pitch uses the same convention: positive =
 * above horizon, 0 = at horizon, -π/2 = nadir, +π/2 = zenith.
 */
export function elevationFromEnu(east: number, north: number, up: number): number {
  if (!Number.isFinite(east) || !Number.isFinite(north) || !Number.isFinite(up)) return 0;
  const range = Math.sqrt(east * east + north * north + up * up);
  if (range === 0) return 0;
  // Clamp before asin so floating-point drift past ±1 doesn't NaN out.
  const ratio = up / range;
  if (ratio >= 1) return Math.PI / 2;
  if (ratio <= -1) return -Math.PI / 2;
  return Math.asin(ratio);
}

/** Convert degrees to radians. Local helper so the math module has
 *  zero external imports. */
export function toRadians(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Convert radians to degrees. */
export function toDegrees(rad: number): number {
  return (rad * 180) / Math.PI;
}

/** Clamp a FOV value (in degrees) to the locked free-look range. Used
 *  by the chunk-2 scroll-wheel-zoom handler to bound user input. */
export function clampFovDeg(fovDeg: number): number {
  if (!Number.isFinite(fovDeg)) return SKY_VIEW_FOV_DEG;
  if (fovDeg < SKY_VIEW_FOV_MIN_DEG) return SKY_VIEW_FOV_MIN_DEG;
  if (fovDeg > SKY_VIEW_FOV_MAX_DEG) return SKY_VIEW_FOV_MAX_DEG;
  return fovDeg;
}

/** Resolve an observer's altitude in meters, applying the sky-view
 *  default when the observer record omits it. */
export function resolveAltitudeMeters(altitudeMeters: number | undefined | null): number {
  if (altitudeMeters === undefined || altitudeMeters === null || !Number.isFinite(altitudeMeters)) {
    return SKY_VIEW_DEFAULT_ALTITUDE_M;
  }
  return altitudeMeters;
}
